import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";
import { PublicLayout } from "../components/public/PublicLayout";
import { ProductCard } from "../components/public/ProductCard";
import { Search, SlidersHorizontal, PackageOpen } from "lucide-react";

const STORAGE_TYPES = ["Ambient", "Frozen", "Chilled"];

export default function Products() {
  const { t, tr } = useLang();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState(params.get("category") || "");
  const [storage, setStorage] = useState("");
  const [dietary, setDietary] = useState("");

  useEffect(() => { document.title = "Products | Sambiva"; }, []);
  useEffect(() => { setCategory(params.get("category") || ""); }, [params]);

  const { data: categories = [] } = useQuery({
    queryKey: ["categories"], queryFn: async () => (await api.get("/categories")).data,
  });
  const { data: products = [], isLoading } = useQuery({
    queryKey: ["products", category, storage, dietary, search],
    queryFn: async () => {
      const q = new URLSearchParams();
      if (category) q.set("category", category);
      if (storage) q.set("storage", storage);
      if (dietary) q.set("dietary", dietary);
      if (search) q.set("search", search);
      return (await api.get(`/products?${q.toString()}`)).data;
    },
  });

  const dietaryOptions = ["Vegan", "Gluten-Free", "Vegetarian"];

  return (
    <PublicLayout>
      <section className="bg-white border-b border-sambiva-charcoal/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
          <h1 className="font-serif text-4xl sm:text-5xl text-sambiva-charcoal mb-3">{t("products.title")}</h1>
          <p className="text-sambiva-muted text-lg max-w-2xl">{t("products.sub")}</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 grid lg:grid-cols-[260px_1fr] gap-8">
        {/* Filters */}
        <aside className="space-y-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-sambiva-muted" size={18} />
            <input
              data-testid="product-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("products.searchPlaceholder")}
              className="w-full pl-10 pr-4 py-3 rounded-xl border border-sambiva-charcoal/15 bg-white focus:outline-none focus:ring-2 focus:ring-sambiva-red/40 text-sm"
            />
          </div>

          <div className="bg-white rounded-2xl p-5 border border-sambiva-charcoal/5">
            <div className="flex items-center gap-2 mb-4 text-sambiva-charcoal font-semibold">
              <SlidersHorizontal size={16} /> {t("common.filters")}
            </div>

            <FilterGroup label={t("common.category")}>
              <FilterPill active={!category} onClick={() => setCategory("")} label={t("common.all")} testid="filter-cat-all" />
              {categories.map((c) => (
                <FilterPill key={c.id} active={category === c.name} onClick={() => setCategory(c.name)} label={c.name} testid={`filter-cat-${c.slug}`} />
              ))}
            </FilterGroup>

            <FilterGroup label={t("common.storage")}>
              <FilterPill active={!storage} onClick={() => setStorage("")} label={t("common.all")} testid="filter-storage-all" />
              {STORAGE_TYPES.map((s) => (
                <FilterPill key={s} active={storage === s} onClick={() => setStorage(s)} label={s} testid={`filter-storage-${s}`} />
              ))}
            </FilterGroup>

            <FilterGroup label={t("common.dietary")}>
              <FilterPill active={!dietary} onClick={() => setDietary("")} label={t("common.all")} testid="filter-diet-all" />
              {dietaryOptions.map((d) => (
                <FilterPill key={d} active={dietary === d} onClick={() => setDietary(d)} label={d} testid={`filter-diet-${d}`} />
              ))}
            </FilterGroup>
          </div>
        </aside>

        {/* Grid */}
        <div>
          {isLoading ? (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="bg-white rounded-3xl aspect-[3/4] animate-pulse" />
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-24" data-testid="products-empty">
              <PackageOpen className="mx-auto text-sambiva-muted mb-4" size={48} />
              <h3 className="font-serif text-2xl text-sambiva-charcoal mb-2">{t("common.noResults")}</h3>
              <p className="text-sambiva-muted">{t("common.tryFilters")}</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" data-testid="products-grid">
              {products.map((p) => (
                <ProductCard key={p.id} product={p} t={t} tr={tr} onView={(slug) => navigate(`/products/${slug}`)} />
              ))}
            </div>
          )}
        </div>
      </div>
    </PublicLayout>
  );
}

function FilterGroup({ label, children }) {
  return (
    <div className="mb-5 last:mb-0">
      <p className="text-xs font-bold uppercase tracking-wide text-sambiva-muted mb-2">{label}</p>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

function FilterPill({ active, onClick, label, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors ${
        active ? "bg-sambiva-red text-white" : "bg-sambiva-cream text-sambiva-charcoal hover:bg-sambiva-red/10"
      }`}
    >
      {label}
    </button>
  );
}
